/**
 * LT-20 — Order list tab (internal / merchant-facing buying quantities).
 *
 * Per material: net qty → wastage % → gross qty → rounded ORDER qty →
 * unit / lengths → rate → cost. Aggregated across the filtered set and
 * rounded ONCE inside each group (never round-then-sum). Filters and
 * grouping are sent verbatim to the PDF export so the print matches the
 * screen. "Show prices" off removes the money columns entirely (screen AND
 * PDF). Rounding here never touches the estimate — the estimate materials
 * total is shown alongside as proof.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, FileDown, FileSpreadsheet, Filter, Loader2, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { gbp, num } from "../../lib/format";
import { Card, EmptyState, PrimaryButton, QuietButton, ToggleGroup } from "../../components/UI";
import { Checkbox } from "../../components/ui/checkbox";
import { WastageInsightsPanel } from "../../components/WastageInsightsPanel";
import { FlatMaterialsPanel } from "../../components/FlatMaterialsPanel";
import { BulkRefreshDialog } from "../../components/BulkRefreshDialog";

const GROUPS = [
  { value: "phase", label: "Phase" },
  { value: "category", label: "Category" },
  { value: "trade", label: "Trade" },
  { value: "none", label: "None" },
];

function _filenameFromHeaders(resp, fallback) {
  const x = resp.headers?.["x-export-filename"] || resp.headers?.["x-pdf-filename"];
  if (x) return x;
  const cd = resp.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return (m && m[1]) || fallback;
}

function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function FilterChips({ label, options, selected, onToggle, testid }) {
  if (!options.length) return null;
  return (
    <div className="flex items-start gap-2 flex-wrap" data-testid={testid}>
      <span className="col-head mt-1.5" style={{ minWidth: 64 }}>{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              data-testid={`${testid}-${o.value}`}
              className={[
                "h-6 px-2 rounded-full border transition-colors",
                on ? "bg-ink text-white border-ink" : "bg-surface text-ink-2 border-line hover:bg-surface-2",
              ].join(" ")}
              style={{ fontSize: 11.5, fontWeight: on ? 600 : 500 }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrderRow({ r, showPrices }) {
  const wastage = r.wastage_pct == null ? `${num(r.wastage_pct_effective)}%*` : `${num(r.wastage_pct)}%`;
  // LT-36 — Flat Material Rate: the money is in, nothing is itemised to order.
  const orderCell = r.flat_material
    ? <span className="text-ink-4 italic" style={{ fontSize: 11 }} data-testid={`order-flat-nothing-${r.id}`}>nothing itemised to order</span>
    : r.lengths != null
    ? (
      <span>
        <span className="font-mono tnum text-ink" style={{ fontWeight: 700 }}>{r.lengths}</span>
        <span className="text-ink-3"> × {num(r.stock_length)} {r.unit}</span>
        <span className="block text-ink-4" style={{ fontSize: 10 }}>{num(r.order_purchase_qty)} {r.unit} for {num(r.total_metres)} {r.unit}</span>
      </span>
    )
    : <span className="font-mono tnum text-ink" style={{ fontWeight: 700 }}>{num(r.order_qty, r.rounding === "none" ? 2 : 0)} <span className="text-ink-3 font-sans" style={{ fontWeight: 400 }}>{r.unit}</span></span>;
  return (
    <tr className="border-t border-line-soft hover:bg-surface-2/40" data-testid={`order-row-${r.id}`}>
      <td className="px-3 py-2 font-mono text-ink-3" style={{ fontSize: 11 }}>{r.code || "—"}</td>
      <td className="px-3 py-2">
        <div className="text-ink flex items-center gap-2" style={{ fontSize: 12.5 }}>
          {r.flat_material && (
            <span className="inline-flex items-center rounded font-mono flex-shrink-0"
              data-testid={`order-row-flat-pill-${r.id}`}
              title="Priced on a Flat Material Rate — line qty × £/unit; no itemised materials behind it"
              style={{ background: "#F8F1DF", color: "#7A5A06", border: "1px solid #EAD9A6", padding: "1px 6px", fontSize: 9, fontWeight: 600, letterSpacing: "0.05em" }}>
              FLAT RATE
            </span>
          )}
          {r.description}
        </div>
        <div className="text-ink-4" style={{ fontSize: 10.5 }}>
          {r.category}{r.line_refs.length ? ` · lines ${r.line_refs.join(", ")}` : ""}
        </div>
        {r.notes.map((n, i) => (
          <div key={i} className="text-[#B45309] inline-flex items-center gap-1" style={{ fontSize: 10.5 }} data-testid={`order-row-note-${r.id}-${i}`}>
            <Info size={10} /> {n}
          </div>
        ))}
      </td>
      <td className="px-3 py-2 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{num(r.net_qty)}</td>
      <td className="px-3 py-2 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }} title={(r.wastage_sources || []).join(", ")}>{wastage}</td>
      <td className="px-3 py-2 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{num(r.gross_qty)}</td>
      <td className="px-3 py-2 text-right" style={{ fontSize: 12 }} data-testid={`order-qty-${r.id}`}>{orderCell}</td>
      {showPrices && (
        <>
          <td className="px-3 py-2 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{gbp(r.rate)}</td>
          <td className="px-3 py-2 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }} data-testid={`order-cost-${r.id}`}>{gbp(r.cost)}</td>
        </>
      )}
    </tr>
  );
}

export function OrderListTab({ tender, onTenderUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [openBulkRefresh, setOpenBulkRefresh] = useState(false);
  const [phaseIds, setPhaseIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trades, setTrades] = useState([]);
  const [groupBy, setGroupBy] = useState("phase");
  const [showPrices, setShowPrices] = useState(true);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    phaseIds.forEach((v) => p.append("phase_id", v));
    categories.forEach((v) => p.append("category", v));
    trades.forEach((v) => p.append("trade", v));
    p.set("group_by", groupBy);
    p.set("show_prices", showPrices ? "true" : "false");
    return p;
  }, [phaseIds, categories, trades, groupBy, showPrices]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/tenders/${tender.id}/order-list?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the order list"))
      .finally(() => setLoading(false));
  }, [tender.id, params]);

  useEffect(() => { load(); }, [load]);

  const toggle = (setter) => (v) => setter((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const exportAs = async (fmt) => {
    setExporting(fmt);
    const path = fmt === "csv" ? "exports/order-list-csv" : "exports/order-list";
    const mime = fmt === "csv" ? "text/csv;charset=utf-8" : "application/pdf";
    try {
      const r = await api.post(`/tenders/${tender.id}/${path}`, {
        phase_ids: phaseIds, categories, trades, group_by: groupBy, show_prices: showPrices,
      }, { responseType: "blob" });
      _download(new Blob([r.data], { type: mime }), _filenameFromHeaders(r, `${tender.ref || tender.id}_Order_List.${fmt}`));
      toast.success(`Order list ${fmt.toUpperCase()}${showPrices ? "" : " (no prices)"} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(null);
    }
  };

  const available = data?.available || { phases: [], categories: [], trades: [] };
  const hasRows = !!(data && data.groups && data.groups.some((g) => g.rows.length));
  const isFiltered = phaseIds.length > 0 || categories.length > 0 || trades.length > 0;

  return (
    <div className="mt-6 space-y-4" data-testid="order-list-tab">
      <Card className="p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Order list</h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 12 }}>
              Buying quantities per material — net → wastage → gross → rounded order qty. Internal and merchant use only; never a client document.
              Rounding here does not change the estimate.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-2 text-ink-2 cursor-pointer mr-1" style={{ fontSize: 12 }} data-testid="order-list-show-prices">
              <Checkbox checked={showPrices} onCheckedChange={(v) => setShowPrices(!!v)} data-testid="order-list-show-prices-checkbox" />
              Show prices
            </label>
            <QuietButton onClick={() => setOpenBulkRefresh(true)} disabled={loading || !data} icon={RefreshCw} data-testid="order-list-refresh-all">
              Refresh all from library
            </QuietButton>
            <QuietButton onClick={() => exportAs("csv")} disabled={!!exporting || loading || !data} icon={exporting === "csv" ? Loader2 : FileSpreadsheet} data-testid="order-list-export-csv">
              Export CSV{showPrices ? "" : " (no prices)"}
            </QuietButton>
            <PrimaryButton onClick={() => exportAs("pdf")} disabled={!!exporting || loading || !data} icon={exporting === "pdf" ? Loader2 : FileDown} data-testid="order-list-export-pdf">
              Export PDF{showPrices ? "" : " (no prices)"}
            </PrimaryButton>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="col-head inline-flex items-center gap-1" style={{ minWidth: 64 }}><Filter size={10} /> Group by</span>
            <ToggleGroup value={groupBy} onChange={setGroupBy} options={GROUPS} />
          </div>
          <FilterChips label="Phase" testid="order-filter-phase" selected={phaseIds} onToggle={toggle(setPhaseIds)}
            options={available.phases.map((p) => ({ value: p.id, label: p.name }))} />
          <FilterChips label="Category" testid="order-filter-category" selected={categories} onToggle={toggle(setCategories)}
            options={available.categories.map((c) => ({ value: c.key, label: c.label }))} />
          <FilterChips label="Trade" testid="order-filter-trade" selected={trades} onToggle={toggle(setTrades)}
            options={available.trades.map((t) => ({ value: t, label: t }))} />
        </div>
      </Card>

      {showPrices && data?.wastage_insights && (
        <WastageInsightsPanel
          tenderId={tender.id}
          insights={data.wastage_insights}
          estimateMaterialsTotal={data.estimate_materials_total}
          filtered={isFiltered}
          storageKey="lt20_wastage_open_orderlist"
        />
      )}

      <BulkRefreshDialog
        open={openBulkRefresh}
        tenderId={tender.id}
        onClose={() => setOpenBulkRefresh(false)}
        onApplied={(t) => { onTenderUpdate?.(t); load(); }}
      />

      {data && data.errors && data.errors.length > 0 && (
        <div className="rounded-lg border border-[#B42318] bg-[#FDE8E6] px-4 py-3" data-testid="order-list-errors">
          <div className="inline-flex items-center gap-1.5 text-[#B42318]" style={{ fontSize: 12.5, fontWeight: 600 }}>
            <AlertTriangle size={13} /> {data.errors.length} line(s) could not be quantified
          </div>
          <ul className="mt-1 space-y-0.5">
            {data.errors.map((e, i) => (
              <li key={i} className="text-[#B42318]" style={{ fontSize: 11.5 }} data-testid={`order-list-error-${e.line_id}`}>
                Line {e.line_ref} ({e.phase_name}) — {e.description}: {e.reason}. Set the coverage in the Materials library, or switch the recipe entry to purchase units.
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && data.flat_material_row_count > 0 && (
        <div className="rounded-lg border border-[#EAD9A6] bg-[#F8F1DF] px-4 py-3 text-[#7A5A06]" style={{ fontSize: 12 }} data-testid="order-list-flat-note">
          <span className="inline-flex items-center gap-1.5" style={{ fontWeight: 600 }}>
            <Info size={13} /> {data.flat_material_row_count} line{data.flat_material_row_count === 1 ? "" : "s"} priced on a Flat Material Rate
            {showPrices && data.flat_material_cost != null ? ` (${gbp(data.flat_material_cost)})` : ""} — nothing itemised to order.
          </span>
          <span className="ml-1 opacity-80">The money is counted in the totals; build the material recipe on the library item (Master Library → Flat material) to get buying quantities.</span>
        </div>
      )}
      {data?.flat_material_report && (
        <FlatMaterialsPanel
          tenderId={tender.id}
          report={data.flat_material_report}
          filtered={isFiltered}
          showPrices={showPrices}
          storageKey="lt36_flat_open_orderlist"
        />
      )}

      {loading && !data ? (
        <div className="text-ink-3" style={{ fontSize: 12 }}>Loading order list…</div>
      ) : !hasRows ? (
        <Card><EmptyState icon={Filter} title="No materials match" hint="Add library lines with recipe materials to the estimate, or widen the filter." /></Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full" data-testid="order-list-table">
            <thead>
              <tr className="bg-surface-2">
                <th className="col-head text-left px-3 py-2" style={{ width: 110 }}>Code</th>
                <th className="col-head text-left px-3 py-2">Material</th>
                <th className="col-head text-right px-3 py-2" style={{ width: 90 }}>Net qty</th>
                <th className="col-head text-right px-3 py-2" style={{ width: 90 }}>Wastage</th>
                <th className="col-head text-right px-3 py-2" style={{ width: 90 }}>Gross qty</th>
                <th className="col-head text-right px-3 py-2" style={{ width: 170 }}>Order qty</th>
                {showPrices && (
                  <>
                    <th className="col-head text-right px-3 py-2" style={{ width: 90 }} data-testid="order-col-rate">Rate £</th>
                    <th className="col-head text-right px-3 py-2" style={{ width: 110 }} data-testid="order-col-cost">Cost £</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g) => (
                <React.Fragment key={g.key}>
                  {groupBy !== "none" && (
                    <tr className="bg-ink text-white" data-testid={`order-group-${g.key}`}>
                      <td colSpan={showPrices ? 8 : 6} className="px-3 py-1.5" style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                        {g.label} <span className="opacity-60 font-normal normal-case tracking-normal">· {g.row_count} material{g.row_count === 1 ? "" : "s"}</span>
                      </td>
                    </tr>
                  )}
                  {g.rows.map((r) => <OrderRow key={r.id} r={r} showPrices={showPrices} />)}
                  {showPrices && groupBy !== "none" && (
                    <tr className="bg-surface-2/70 border-t border-line" data-testid={`order-group-subtotal-${g.key}`}>
                      <td colSpan={7} className="px-3 py-1.5 text-right text-ink-2" style={{ fontSize: 11.5, fontWeight: 600 }}>{g.label} subtotal</td>
                      <td className="px-3 py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 700 }}>{gbp(g.subtotal_cost)}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {showPrices && (
              <tfoot>
                <tr className="border-t-2 border-ink">
                  <td colSpan={7} className="px-3 py-2.5 text-right text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>Order total (rounded buying quantities)</td>
                  <td className="px-3 py-2.5 text-right font-mono tnum text-ink" style={{ fontSize: 14, fontWeight: 700 }} data-testid="order-list-total">{gbp(data.total_cost)}</td>
                </tr>
                <tr>
                  <td colSpan={8} className="px-3 pb-3 text-right text-ink-4" style={{ fontSize: 11 }} data-testid="order-list-estimate-total">
                    Estimate materials total {gbp(data.estimate_materials_total)} — unchanged by order rounding. * wastage varies between lines; effective % shown.
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </Card>
      )}
    </div>
  );
}
